import { useEffect } from 'react';

import { type Kitchen } from '@rta/rta-blueprint-library';

import { type ComponentController } from '@r-core/controller';
import { type SkuAnnotationTool } from './sku-annotation-toolbar';

export type UseSkuAnnotationEventsParams = {
	controller: ComponentController;
	kitchen: Kitchen;
	selectedAnnotationId: string | null;
	setSelectedAnnotationId: (id: string | null) => void;
	setEditingAnnotationId: (id: string | null) => void;
	setActiveTool: (tool: SkuAnnotationTool) => void;
};

/**
 * Subscribes to SKU annotation tool and selection events on the controller.
 * Handles delete, tool switching, and annotation selection via publish/subscribe.
 */
export const useSkuAnnotationEvents = (params: UseSkuAnnotationEventsParams): void => {
	const { controller, kitchen, selectedAnnotationId, setSelectedAnnotationId, setEditingAnnotationId, setActiveTool } = params;

	useEffect(() => {
		const handleAnnotationToolEvent = (event: { payload: { tool: string } }): void => {
			const { tool } = event.payload;

			if (tool === 'delete') {
				if (selectedAnnotationId) {
					kitchen.skuAnnotations.delete(selectedAnnotationId);
					setSelectedAnnotationId(null);
					setEditingAnnotationId(null);
				}
			} else if (tool === 'textbox' || tool === 'doodle' || tool === 'line' || tool === 'rectangle' || tool === 'ellipse') {
				setActiveTool(tool);
			} else if (tool === 'select') {
				setActiveTool('select');
			}
		};

		const handleAnnotationSelectEvent = (event: { payload: { annotationId: string } }): void => {
			const { annotationId } = event.payload;
			if (annotationId) {
				setSelectedAnnotationId(annotationId);
			}
		};

		const subscriptionId = controller.subscribe('sku-annotation-tool', 'delete', handleAnnotationToolEvent);
		const subscriptionIdTextbox = controller.subscribe('sku-annotation-tool', 'textbox', handleAnnotationToolEvent);
		const subscriptionIdDoodle = controller.subscribe('sku-annotation-tool', 'doodle', handleAnnotationToolEvent);
		const subscriptionIdLine = controller.subscribe('sku-annotation-tool', 'line', handleAnnotationToolEvent);
		const subscriptionIdRectangle = controller.subscribe('sku-annotation-tool', 'rectangle', handleAnnotationToolEvent);
		const subscriptionIdEllipse = controller.subscribe('sku-annotation-tool', 'ellipse', handleAnnotationToolEvent);
		const subscriptionIdSelect = controller.subscribe('sku-annotation-tool', 'select', handleAnnotationToolEvent);
		const subscriptionIdAnnotationSelect = controller.subscribe('sku-annotation-select', 'select', handleAnnotationSelectEvent);

		return () => {
			controller.unsubscribe(subscriptionId);
			controller.unsubscribe(subscriptionIdTextbox);
			controller.unsubscribe(subscriptionIdDoodle);
			controller.unsubscribe(subscriptionIdLine);
			controller.unsubscribe(subscriptionIdRectangle);
			controller.unsubscribe(subscriptionIdEllipse);
			controller.unsubscribe(subscriptionIdSelect);
			controller.unsubscribe(subscriptionIdAnnotationSelect);
		};
	}, [controller, kitchen, selectedAnnotationId, setSelectedAnnotationId, setEditingAnnotationId, setActiveTool]);
};
